import { useLocale } from '@mirai/locale';
import { Notification, Progress, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { CONTAINER_ID, SCRIPT_ID, SCRIPT_URL } from './FormPaymob.constants';
import { L10N } from './FormPaymob.l10n';
import * as style from './FormPaymob.module.css';
import { getStyle } from './helpers/getStyle';
import { isPaymentSuccess } from './helpers/isPaymentSuccess';
import { ERROR } from '../../../../../Core.constants';
import { loadScript } from '../../../../../helpers/loadScript';

export const FormPaymob = ({ response, testId }) => {
  const { translate } = useLocale();

  const [error, setError] = useState();
  const [ready, setReady] = useState();

  useEffect(() => {
    let mounted = true;
    let cleanup;

    (async () => {
      try {
        const nextCleanup = await loadScript({
          domain: 'payments',
          id: SCRIPT_ID,
          src: SCRIPT_URL,
          onLoad: handleLoad,
        });
        if (!mounted) return nextCleanup();

        cleanup = nextCleanup;
        if (window.Pixel) setReady(true);
      } catch (nextError) {
        if (mounted) setError(nextError);
      }
    })();

    return () => {
      mounted = false;
      cleanup && cleanup();
    };
  }, []);

  useEffect(() => {
    if (!response || !ready) return;

    setError();

    const { configuration: { clientSecret, paymentMethods, paymentTransactionId, publicKey, returnUrl } = {} } =
      response;
    let active = true;
    try {
      new window.Pixel({
        publicKey,
        clientSecret,
        paymentMethods,
        elementId: CONTAINER_ID,
        afterPaymentComplete: (result) => {
          if (!active) return;

          if (!isPaymentSuccess(result)) {
            setError(ERROR.UNKNOWN);
            return;
          }

          try {
            const url = new URL(returnUrl);
            paymentTransactionId && url.searchParams.append('paymentTransactionId', paymentTransactionId);

            window.location.href = url.toString();
          } catch (nextError) {
            setError(nextError);
          }
        },
        customStyle: getStyle(),
      });
    } catch (nextError) {
      setError(nextError);
    }

    return () => {
      active = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, response]);

  const handleLoad = () => {
    setReady(true);
  };

  return (
    <View testId={testId}>
      <Progress indeterminate visible={!ready && !error} />
      <View id={CONTAINER_ID} className={style.container} testId={testId ? `${testId}-container` : undefined} />
      {error && (
        <Notification error large wide testId={testId ? `${testId}-notification` : undefined}>
          {translate(L10N.NOTIFICATION_ERROR)}
        </Notification>
      )}
    </View>
  );
};

FormPaymob.displayName = 'Mirai:Payments:ButtonPayment:FormPaymob';

FormPaymob.propTypes = {
  response: PropTypes.shape({
    configuration: PropTypes.shape({
      clientSecret: PropTypes.string,
      paymentMethods: PropTypes.arrayOf(PropTypes.string),
      paymentTransactionId: PropTypes.string,
      publicKey: PropTypes.string,
      returnUrl: PropTypes.string,
    }),
  }),
  testId: PropTypes.string,
};
